import React from 'react';
import { Gauge, ArrowLeft, FileText } from 'lucide-react';
import { Markdown } from '@com.yanicksenn/shared';
import { useI18n } from '@/context/I18nContext';
import { useRouter } from '@/context/RouterContext';
import { Footer } from '@/components/navigation/Footer';
import { LanguageDropdown } from '@shared/auth/LanguageDropdown';
import type { Locale } from '@/i18n';
import termsEnMarkdown from '@resources/terms_and_conditions.en.md';
import termsDeMarkdown from '@resources/terms_and_conditions.de.md';
import termsFrMarkdown from '@resources/terms_and_conditions.fr.md';
import termsEsMarkdown from '@resources/terms_and_conditions.es.md';
import termsItMarkdown from '@resources/terms_and_conditions.it.md';
import termsNlMarkdown from '@resources/terms_and_conditions.nl.md';

const TERMS_MARKDOWN_BY_LOCALE: Record<Locale, string> = {
  en: termsEnMarkdown,
  de: termsDeMarkdown,
  fr: termsFrMarkdown,
  es: termsEsMarkdown,
  it: termsItMarkdown,
  nl: termsNlMarkdown,
};

export const TermsPage: React.FC = () => {
  const { t, locale, setLocale } = useI18n();
  const { navigate } = useRouter();

  const markdownContent = TERMS_MARKDOWN_BY_LOCALE[locale] ?? termsEnMarkdown;

  return (
    <div className="min-h-screen flex flex-col bg-bg-base text-text-primary" data-testid="terms-page">
      <header className="nav-bar h-[60px] sm:h-[52px] sticky top-0 z-40">
        <div className="h-full max-w-[960px] mx-auto px-5 sm:px-8 flex items-center justify-between">
          <a
            href="#/"
            onClick={(e) => {
              e.preventDefault();
              navigate('#/');
            }}
            className="flex items-center gap-2.5 text-text-primary hover:opacity-90 transition-opacity"
          >
            <Gauge size={18} className="text-accent" />
            <span className="text-sm font-semibold tracking-tight">kart.win</span>
          </a>

          <div className="flex items-center gap-2">
            <LanguageDropdown
              locale={locale}
              onChange={setLocale}
              testIdPrefix="terms-page-locale"
            />
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-[820px] mx-auto px-5 sm:px-8 pt-8 pb-12">
        <a
          href="#/"
          onClick={(e) => {
            e.preventDefault();
            navigate('#/');
          }}
          data-testid="terms-page-back"
          className="inline-flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors mb-4 cursor-pointer"
        >
          <ArrowLeft size={14} />
          <span>{t('common.back')}</span>
        </a>

        <div className="card p-6 sm:p-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-medium mb-4">
            <FileText size={13} />
            <span>{t('footer.termsAndConditions')}</span>
          </div>

          <Markdown content={markdownContent} />
        </div>
      </main>

      <Footer />
    </div>
  );
};
